import { createSlice } from '@reduxjs/toolkit';
import pluralize from 'pluralize';
import sdk from '../../sdk';
import updateTeammate from '../helpers/teammates/updateTeammate';
import normalizeUsers from '../helpers/teammates/normalizeUsers';
import sortTeammates from '../helpers/teammates/sortTeammates';
import Logger from '../../services/Logger';
import l18n from '../../shared/strings';
import * as userActions from '../actions/user';
import { showDialog, showErrorDialog } from '../../components/dialog';
import * as utils from '../../shared/utils';
import Toast from '../../components/Toast';

const CANCELLED = 'cancelled';

const teamSlice = createSlice({
  name: 'teammates',
  initialState: {
    loading: false,
    processingIds: [],
    roleChangingIds: [],
    error: null,
    items: [],
  },
  reducers: {
    getTeamStart(state) {
      state.loading = true;
      state.error = null;
    },
    getTeamSuccess(state, action) {
      const { items } = action.payload;
      state.items = items;
      state.loading = false;
      state.error = null;
    },
    getTeamFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },

    // Add teammate
    addTeammatesStart(state) {
      state.loading = true;
      state.error = null;
    },
    addTeammatesSuccess(state, action) {
      const { items } = action.payload;
      state.items = items;
      state.loading = false;
      state.error = null;
    },
    addTeammatesFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },

    // Remove teammate
    removeTeammateStart(state, action) {
      const { ids } = action.payload;
      state.loading = true;
      state.processingIds = [...state.processingIds, ...ids];
      state.error = null;
    },
    removeTeammateSuccess(state, action) {
      const { items, ids } = action.payload;
      state.loading = false;
      state.processingIds = state.processingIds.filter((id) => !ids.includes(id));
      state.items = items;
      state.error = null;
    },
    removeTeammateFailure(state, action) {
      const { ids } = action.payload;
      state.loading = false;
      state.processingIds = state.processingIds.filter((id) => !ids.includes(id));
      state.error = action.payload;
    },

    // Assign role
    assignRoleStart(state, action) {
      const { ids } = action.payload;
      state.loading = true;
      state.roleChangingIds = [...state.roleChangingIds, ...ids];
      state.error = null;
    },
    assignRoleSuccess(state, action) {
      const { items, ids } = action.payload;
      state.items = items;
      state.loading = false;
      state.roleChangingIds = state.roleChangingIds.filter((id) => !ids.includes(id));
      state.error = null;
    },
    assignRoleFailure(state, action) {
      const { ids, err } = action.payload;
      state.loading = false;
      state.roleChangingIds = state.roleChangingIds.filter((id) => !ids.includes(id));
      state.error = err;
    },

    // Confirm teammate
    confirmTeammateStart(state) {
      state.loading = true;
      state.error = null;
    },
    confirmTeammateSuccess(state, action) {
      const { items } = action.payload;
      state.items = items;
      state.loading = false;
      state.error = null;
    },
    confirmTeammateFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },

    // Reject teammate
    rejectTeammateStart(state) {
      state.loading = true;
      state.error = null;
    },
    rejectTeammateSuccess(state, action) {
      const { items } = action.payload;
      state.items = items;
      state.loading = false;
      state.error = null;
    },
    rejectTeammateFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },

    // Resend invite to teammate
    resendInviteTeammateStart(state) {
      state.loading = true;
      state.error = null;
    },
    resendInviteTeammateSuccess(state, action) {
      const { items } = action.payload;
      state.items = items;
      state.loading = false;
      state.error = null;
    },
    resendInviteTeammateFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    setTeammateStatus(state, { payload }) {
      const idx = state.items.findIndex(({ _id }) => _id === payload.userId);
      state.items[idx] = {
        ...state.items[idx],
        ...payload.statusData,
      };
    },

    // Update teammate
    updateTeammateOptimistic: updateTeammate,
    updateTeammateFailure: updateTeammate,
  },
});

export const {
  getTeamStart,
  getTeamSuccess,
  getTeamFailure,
  addTeammatesStart,
  addTeammatesSuccess,
  addTeammatesFailure,
  removeTeammateStart,
  removeTeammateSuccess,
  removeTeammateFailure,
  assignRoleStart,
  assignRoleSuccess,
  assignRoleFailure,
  confirmTeammateStart,
  confirmTeammateSuccess,
  confirmTeammateFailure,
  rejectTeammateStart,
  rejectTeammateSuccess,
  rejectTeammateFailure,
  resendInviteTeammateStart,
  resendInviteTeammateSuccess,
  resendInviteTeammateFailure,
  updateTeammateOptimistic,
  updateTeammateFailure,
  setTeammateStatus,
} = teamSlice.actions;

export default teamSlice.reducer;

export const getTeam = () => async (dispatch, getAll) => {
  try {
    dispatch(getTeamStart());
    const { data: items } = await sdk.teammates.getAll();
    const normalizedTeammates = normalizeUsers(items, getAll().roles.items);
    dispatch(getTeamSuccess({ items: normalizedTeammates }));
  } catch (err) {
    dispatch(getTeamFailure(err));
  }
};

export const addTeammates = (data, force = false) => async (dispatch, getAll) => {
  try {
    dispatch(addTeammatesStart());
    const { data: invitedTeammates } = await sdk.teammates.create(data, force);
    const result = {
      addedTeammates: [],
      notAddedTeammates: [],
      invitedTeammates,
    };
    if (!invitedTeammates) {
      showErrorDialog(l18n.COLLECTIONS.errorTeammateWasntAdded);
      return;
    }
    if (invitedTeammates.length > 1) {
      invitedTeammates.forEach((teammate) => {
        if (teammate.success === false) {
          result.notAddedTeammates.push(teammate);
        } else {
          result.addedTeammates.push(teammate);
        }
      });
    } else if (invitedTeammates.length === 1 && invitedTeammates[0].success === false) {
      result.notAddedTeammates.push(invitedTeammates[0]);
    } else {
      const teammate = invitedTeammates[0];
      teammate.act = true;
      result.addedTeammates.push(teammate);
    }

    const currentTeammates = getAll().teammates.items;
    const { subscriptionFeatures, addonsFeatures = {} } = getAll().user;
    const items = [...currentTeammates, ...normalizeUsers(result.addedTeammates, getAll().roles.items)].sort(sortTeammates);
    dispatch(addTeammatesSuccess({ items }));
    result.invitedTeammates = normalizeUsers(result.invitedTeammates, getAll().roles.items);
    const copyAddonsFeatures = { ...addonsFeatures };
    const copySubscriptionFeatures = { ...subscriptionFeatures };

    if (force) {
      const limit = subscriptionFeatures.teammatesLimit;
      const pendingCount = subscriptionFeatures.teammatesCountIncludingPending;
      const newUsersCount = result.addedTeammates.length;
      const seatsToBuy = newUsersCount - (limit - pendingCount);

      copyAddonsFeatures.teammatesLimit = addonsFeatures?.teammatesLimit
        ? copyAddonsFeatures.teammatesLimit + seatsToBuy
        : seatsToBuy;
      copySubscriptionFeatures.teammatesLimit += seatsToBuy;

      Toast(l18n.TOAST.UPGRADE_ADDON_SUCCESS(`${pluralize('teammate', seatsToBuy, true)}`));
    }

    dispatch(userActions.updateUser({
      addonsFeatures: {
        ...copyAddonsFeatures,
      },
      subscriptionFeatures: {
        ...copySubscriptionFeatures,
        teammatesCountIncludingPending: items.filter((teammate) => teammate.status !== 'Requested').length - 1,
      },
    }));
    return result;
  } catch (err) {
    const errorMessage = utils.getDataFromResponceError(err, 'msg') || 'Can not add teammate';
    dispatch(addTeammatesFailure(err));
    showErrorDialog(errorMessage);
    Logger.error(new Error('Error apply invite'), { error: err }, [
      'ApplyInviteFailed',
      (err && err.message) || 'NoMessage',
    ]);
  }
};

export const removeTeammate = (id) => async (dispatch, getAll) => {
  const currentTeammates = getAll().teammates.items;
  const teammateToRemove = currentTeammates.find((teammate) => teammate._id === id);
  Logger.log('Ui', 'RemoveTeammateDialogShow', { id, email: teammateToRemove.email });
  showDialog({
    title: l18n.TEAMMATES.textTitleRemoveTeammate,
    text: l18n.TEAMMATES.textTeammateWillRemoved,
    textBtnOk: l18n.DIALOGS.btnRemove,
    textBtnCancel: l18n.DIALOGS.btnCancel,
    onCancel: () => {
      Logger.log('User', 'RemoveTeammateDialogNo');
    },
    onOk: async () => {
      Logger.log('User', 'RemoveTeammateDialogYes', teammateToRemove.email);
      try {
        dispatch(removeTeammateStart({ ids: [id] }));
        const { data: result } = await sdk.teammates.delete(teammateToRemove.email);
        if (result.success) {
          dispatch(
            removeTeammateSuccess({ ids: [id], items: currentTeammates.filter((teammate) => teammate._id !== id) }),
          );
          const { subscriptionFeatures } = getAll().user;
          const updatedSubscriptionFeatures = {
            ...subscriptionFeatures,
            teammatesCountIncludingPending: subscriptionFeatures.teammatesCountIncludingPending - 1,
          };
          if (teammateToRemove.status === 'Accepted') {
            updatedSubscriptionFeatures.teammatesCount = subscriptionFeatures.teammatesCount - 1;
          }
          dispatch(userActions.updateUser({
            subscriptionFeatures: updatedSubscriptionFeatures,
          }));
        } else {
          dispatch(removeTeammateFailure({ ids: [id] }));
        }
      } catch (err) {
        showErrorDialog(l18n.TEAMMATES.textCantRemoveTeammate);
        dispatch(removeTeammateFailure({ ids: [id], err }));
      }
    },
  });
};

export const assignRole = (roleId, teammatesIds) => async (dispatch, getAll) => {
  try {
    dispatch(assignRoleStart({ ids: teammatesIds }));
    const roles = getAll().roles.items;
    const newRole = roles.find((role) => role._id === roleId);
    const teammates = [];
    const currentTeammates = getAll().teammates.items;
    currentTeammates.forEach((teammate) => {
      if (teammatesIds.includes(teammate._id)) {
        const oldRole = roles.find((role) => role._id === teammate.parent.teammateRoleId);
        teammates.push({
          _id: teammate._id,
          displayName: teammate.displayName,
          oldRole: {
            _id: oldRole._id,
            name: oldRole.name,
          },
        });
      }
    });

    const data = {
      teammates,
      newRole: {
        _id: roleId,
        name: newRole.name,
      },
    };

    try {
      const { data: result } = await sdk.teammates.assignRoleMultiple(data);

      if (result.affectedAssetsCount) {
        await new Promise((resolve, reject) => {
          const {
            TITLE, TEXT_MULTIPLE, OK_TEXT, CANCEL_TEXT,
          } = l18n.DIALOGS.ASSIGN_ROLE_WARNING;
          showDialog({
            title: TITLE,
            text: TEXT_MULTIPLE,
            onOk: async () => {
              await sdk.teammates.assignRoleMultiple({ ...data, force: true });
              resolve();
            },
            textBtnOk: OK_TEXT,
            onCancel: () => {
              reject(CANCELLED);
            },
            textBtnCancel: CANCEL_TEXT,
          });
        });
      }
      const teammatesWithNewRole = currentTeammates.map((teammate) => {
        if (teammatesIds.includes(teammate._id)) {
          const teammateForUpdate = { ...teammate };
          teammateForUpdate.parent = { ...teammateForUpdate.parent };
          teammateForUpdate.parent.teammateRoleId = roleId;
          teammateForUpdate.roleId = roleId;
          teammateForUpdate.roleName = newRole.name;
          return teammateForUpdate;
        }
        return teammate;
      });
      dispatch(assignRoleSuccess({ items: teammatesWithNewRole, ids: teammatesIds }));
    } catch (err) {
      if (err === CANCELLED) {
        Logger.info('Change role for items cancelled by initiator');
      } else {
        Logger.error(new Error('Can not assign role to multiple items'), { error: err, showDialog: true }, [
          'AssignRoleMultipleFailed',
        ]);
      }
      return;
    }
  } catch (err) {
    dispatch(assignRoleFailure({ ids: teammatesIds, err }));
  }
};

export const confirmTeammate = (id, force = false) => async (dispatch, getAll) => {
  Logger.log('User', 'SettingsMyTeamConfirmTeammate');
  try {
    const { subscriptionFeatures, addonsFeatures = {} } = getAll().user;
    dispatch(confirmTeammateStart({ id }));
    const copyAddonsFeatures = { ...addonsFeatures };
    const copySubscriptionFeatures = { ...subscriptionFeatures };

    const currentTeammates = getAll().teammates.items;
    const { ...teammateToConfirm } = currentTeammates.find((teammate) => teammate._id === id);
    teammateToConfirm.parent = { ...teammateToConfirm.parent };
    delete teammateToConfirm.parent.confirmedByTeam;
    teammateToConfirm.parent.confirmed = true;
    const { data: result } = await sdk.teammates.confirmRequest(id, force);
    const items = insertNewItem(currentTeammates, ...normalizeUsers([teammateToConfirm], getAll().roles.items));
    if (result.success) {
      dispatch(
        confirmTeammateSuccess({
          items,
        }),
      );
      if (force) {
        copyAddonsFeatures.teammatesLimit = addonsFeatures?.teammatesLimit
          ? copyAddonsFeatures.teammatesLimit + 1
          : 1;
        copySubscriptionFeatures.teammatesLimit += 1;

        Toast(l18n.TOAST.UPGRADE_ADDON_SUCCESS(`${pluralize('teammate', 1, true)}`));
      }

      dispatch(userActions.updateUser({
        addonsFeatures: {
          ...copyAddonsFeatures,
        },
        subscriptionFeatures: {
          ...copySubscriptionFeatures,
          teammatesCount: subscriptionFeatures.teammatesCount + 1,
          teammatesCountIncludingPending: subscriptionFeatures.teammatesCountIncludingPending + 1,
        },
      }));
    } else {
      dispatch(confirmTeammateFailure({ id }));
    }
  } catch (err) {
    showErrorDialog(l18n.TEAMMATES.textCantConfirmTeammate);
    Logger.error(new Error('Error confirm teammate'), { Amplitude: 'CantConfirmTeammate', error: err }, [
      'CantConfirmTeammate',
      (err && err.message) || 'NoMessage',
    ]);
    dispatch(confirmTeammateFailure({ id, err }));
  }
};

export const rejectTeammate = (id) => async (dispatch, getAll) => {
  Logger.log('User', 'SettingsMyTeamRejectTeammate');
  const currentTeammates = getAll().teammates.items;

  try {
    dispatch(rejectTeammateStart({ id }));
    const { data: result } = await sdk.teammates.rejectRequest(id);
    if (result.success) {
      dispatch(rejectTeammateSuccess({ items: currentTeammates.filter((teammate) => teammate._id !== id) }));
    } else {
      dispatch(rejectTeammateFailure({ id }));
    }
  } catch (err) {
    showErrorDialog(l18n.TEAMMATES.textCantRejectTeammate);
    Logger.error(new Error('Error reject teammate'), { Amplitude: 'CantRejectTeammate', error: err }, [
      'CantRejectTeammate',
      (err && err.message) || 'NoMessage',
    ]);
    dispatch(rejectTeammateFailure({ id, err }));
  }
};

export const resendInviteTeammate = (id) => async (dispatch, getAll) => {
  Logger.log('User', 'SettingsMyTeamReinviteTeammate');
  const currentTeammates = getAll().teammates.items;
  const { ...teammateToModify } = currentTeammates.find((teammate) => teammate._id === id);
  teammateToModify.parent = { ...teammateToModify.parent };
  teammateToModify.parent.reinvited = true;

  try {
    dispatch(rejectTeammateStart({ id }));
    const { data: result } = await sdk.teammates.resendInvitation(teammateToModify.email);
    if (result.success) {
      dispatch(
        rejectTeammateSuccess({
          items: insertNewItem(currentTeammates, ...normalizeUsers([teammateToModify], getAll().roles.items)),
        }),
      );
    } else {
      dispatch(rejectTeammateFailure({ id }));
    }
  } catch (err) {
    showErrorDialog('Cannot reinvite teammate');
    Logger.error(new Error('Error reject teammate'), { Amplitude: 'CantReinviteTeammate', error: err }, [
      'CantReinviteTeammate',
      (err && err.message) || 'NoMessage',
    ]);
    dispatch(rejectTeammateFailure({ id, err }));
  }
};

function insertNewItem(array, newItem) {
  return array.map((item) => {
    if (item._id !== newItem._id) {
      // This isn't the item we care about - keep it as-is
      return item;
    }
    // Otherwise, this is the one we want - return an updated value
    return {
      ...newItem,
    };
  });
}

export const updateTeammateByField = (payload) => async (dispatch, getAll) => {
  Logger.log('User', 'UpdateTeammate', payload);

  const { teammateId, field, value } = payload;
  const teammates = getAll().teammates.items;
  const index = teammates.findIndex(({ _id }) => _id === teammateId);
  const prevValue = teammates[index][field];
  const apiSchema = {
    displayName: 'rename',
    phone: 'changePhone',
    position: 'changePosition',
    slackUserId: 'changeSlackUserId',
  };
  const requestName = apiSchema[field];

  try {
    dispatch(updateTeammateOptimistic({ index, field, value }));

    if (field === 'twoFactorConfigured') {
      const { data } = await sdk.auth.resetTwoFactor(teammateId);
      return data;
    }

    const { data } = await sdk.teammates[requestName](teammateId, value);
    return data;
  } catch (e) {
    dispatch(updateTeammateFailure({ index, field, value: prevValue }));

		showErrorDialog(l18n.TEAMMATES.textCantUpdateTeammate); // eslint-disable-line
    Logger.error(new Error('Error reject update teammate'), { Amplitude: 'CantUpdateTeammate', error: e }, [
      'CantUpdateTeammate',
      (e && e.message) || 'NoMessage',
    ]);
  }
};
